import React from 'react';
import { IconButton, Select, MenuItem } from '@material-ui/core';
import { FirstPage, LastPage, ChevronLeft, ChevronRight } from '@material-ui/icons';

export interface TablePaginationProps {
    page: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    pageSizeOptions?: number[];
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 500, 1000];

const TablePagination: React.FC<TablePaginationProps> = ({
    page,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}) => {
    const from = totalCount === 0 ? 0 : page * pageSize + 1;
    const to = totalCount === 0 ? 0 : Math.min(totalCount, (page + 1) * pageSize);
    const lastPage = totalCount <= 0 ? 0 : Math.max(0, Math.ceil(totalCount / pageSize) - 1);
    const isFirstPage = page <= 0;
    const isLastPage = page >= lastPage;

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
                backgroundColor: '#fff',
                borderTop: '1px solid #e0e0e0',
                padding: '8px 16px',
                fontSize: 14,
                color: 'rgba(0, 0, 0, 0.54)',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Rows per page selector - "20 rows" style */}
                <Select
                    value={pageSize}
                    onChange={(e) => {
                        const val = e.target.value as number;
                        if (val != null && val >= 1) {
                            onPageSizeChange(Number(val));
                            onPageChange(0);
                        }
                    }}
                    disableUnderline
                    style={{
                        fontSize: 14,
                        color: 'rgba(0, 0, 0, 0.87)',
                        marginRight: 0,
                        minWidth: 72,
                    }}
                    SelectDisplayProps={{
                        style: {
                            display: 'flex',
                            alignItems: 'center',
                            paddingRight: 24,
                        },
                    }}
                    MenuProps={{
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                    }}
                    renderValue={(val) => `${val} rows`}
                >
                    {pageSizeOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                            {opt} rows
                        </MenuItem>
                    ))}
                </Select>

                {/* Vertical separator */}
                <div
                    style={{
                        width: 1,
                        height: 24,
                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                        margin: '0 8px',
                    }}
                />

                {/* Page navigation */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <IconButton
                        size="small"
                        onClick={() => onPageChange(0)}
                        disabled={isFirstPage}
                        aria-label="Go to first page"
                        style={{
                            padding: 4,
                            color: isFirstPage ? 'rgba(0, 0, 0, 0.26)' : 'rgba(0, 0, 0, 0.54)',
                        }}
                    >
                        <FirstPage fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => onPageChange(page - 1)}
                        disabled={isFirstPage}
                        aria-label="Go to previous page"
                        style={{
                            padding: 4,
                            color: isFirstPage ? 'rgba(0, 0, 0, 0.26)' : 'rgba(0, 0, 0, 0.54)',
                        }}
                    >
                        <ChevronLeft fontSize="small" />
                    </IconButton>
                    <span
                        style={{
                            margin: '0 12px',
                            color: 'rgba(0, 0, 0, 0.54)',
                            fontSize: 14,
                            minWidth: 80,
                            textAlign: 'center',
                        }}
                    >
                        {totalCount === 0 ? '0-0 of 0' : `${from}-${to} of ${totalCount}`}
                    </span>
                    <IconButton
                        size="small"
                        onClick={() => onPageChange(page + 1)}
                        disabled={isLastPage}
                        aria-label="Go to next page"
                        style={{
                            padding: 4,
                            color: isLastPage ? 'rgba(0, 0, 0, 0.26)' : 'rgba(0, 0, 0, 0.54)',
                        }}
                    >
                        <ChevronRight fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => onPageChange(lastPage)}
                        disabled={isLastPage}
                        aria-label="Go to last page"
                        style={{
                            padding: 4,
                            color: isLastPage ? 'rgba(0, 0, 0, 0.26)' : 'rgba(0, 0, 0, 0.54)',
                        }}
                    >
                        <LastPage fontSize="small" />
                    </IconButton>
                </div>
            </div>
        </div>
    );
};

export default React.memo(TablePagination);
